// Composants de base, partagés par toutes les pages.
import {
  createContext, useCallback, useContext, useEffect, useId, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { t } from "../lib/i18n";

const cx =(...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");
export { cx };

// ---------------------------------------------------------------- boutons
type Variant = "primary" | "secondary" | "ghost" | "danger";
const VARIANTS: Record<Variant, string> = {
  primary: "bg-accent text-bg hover:bg-accent/85 font-semibold",
  secondary: "bg-raised text-ink border border-line hover:border-faint",
  ghost: "text-muted hover:text-ink hover:bg-raised",
  danger: "bg-err/15 text-err border border-err/40 hover:bg-err/25",
};

export function Button({ variant = "secondary", size = "md", className, ...props }:
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: "sm" | "md" }) {
  return (
    <button
      type="button"
      {...props}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 rounded-md transition-colors whitespace-nowrap",
        "disabled:opacity-40 disabled:pointer-events-none cursor-pointer",
        size === "sm" ? "h-7 px-2.5 text-[13px]" : "h-9 px-3.5",
        VARIANTS[variant], className,
      )}
    />
  );
}

// ---------------------------------------------------------------- pastilles d'état
export type Tone = "ok" | "warn" | "err" | "muted" | "accent";
const TONES: Record<Tone, { text: string; dot: string; bg: string }> = {
  ok: { text: "text-ok", dot: "bg-ok", bg: "bg-ok/12" },
  warn: { text: "text-warn", dot: "bg-warn", bg: "bg-warn/12" },
  err: { text: "text-err", dot: "bg-err", bg: "bg-err/12" },
  muted: { text: "text-muted", dot: "bg-faint", bg: "bg-raised" },
  accent: { text: "text-accent", dot: "bg-accent", bg: "bg-accent/12" },
};

export function Dot({ tone, pulse = false }: { tone: Tone; pulse?: boolean }) {
  return (
    <span className="relative inline-flex size-2 shrink-0">
      {pulse && <span className={cx("absolute inset-0 rounded-full opacity-60 animate-ping", TONES[tone].dot)} />}
      <span className={cx("relative size-2 rounded-full", TONES[tone].dot)} />
    </span>
  );
}

export function Pill({ tone, children, pulse }: { tone: Tone; children: ReactNode; pulse?: boolean }) {
  return (
    <span className={cx("inline-flex items-center gap-1.5 rounded px-2 py-0.5 font-cond text-[12px] font-semibold uppercase tracking-wide whitespace-nowrap",
      TONES[tone].text, TONES[tone].bg)}>
      <Dot tone={tone} pulse={pulse} />
      {children}
    </span>
  );
}

export function Tag({ children, caps = true }: { children: ReactNode; caps?: boolean }) {
  return (
    <span className={cx("inline-flex items-center rounded border border-line px-1.5 py-px font-mono text-[11px] text-muted", caps && "uppercase")}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------- mise en page
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="font-cond text-2xl font-semibold tracking-wide text-balance">{title}</h1>
        {subtitle && <p className="mt-0.5 text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </header>
  );
}

export function Panel({ title, actions, children, className }:
  { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("rounded-lg border border-line bg-surface", className)}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
          <h2 className="font-cond text-[13px] font-semibold uppercase tracking-wider text-muted">{title}</h2>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-line px-6 py-12 text-center">
      <p className="font-cond text-lg font-semibold">{title}</p>
      {children && <p className="max-w-md text-muted">{children}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Stat({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: Tone }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="font-cond text-[12px] font-semibold uppercase tracking-wider text-faint">{label}</span>
      <span className={cx("num text-lg font-medium", tone && TONES[tone].text)}>{value}</span>
      {sub && <span className="num text-[12px] text-muted">{sub}</span>}
    </div>
  );
}

// ---------------------------------------------------------------- fenêtres
export function Modal({ open, title, onClose, children, footer, wide = false }: {
  open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  const titleId = useId();
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  // Rendue à la racine de la page (portail) : ouverte depuis la barre du haut, dont le flou (backdrop-filter)
  // sert de référence aux éléments « fixed », la fenêtre serait sinon enfermée dans les 80 px de la barre.
  return createPortal(
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-10"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId}
        className={cx("w-full rounded-lg border border-line bg-surface shadow-2xl", wide ? "max-w-3xl" : "max-w-lg")}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3">
          <h2 id={titleId} className="font-cond text-lg font-semibold tracking-wide">{title}</h2>
          <button type="button" onClick={onClose} aria-label={t("Fermer")}
            className="rounded p-1 text-muted hover:bg-raised hover:text-ink cursor-pointer">✕</button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation intégrée (les boîtes confirm() du navigateur bloquent la page). */
export function ConfirmDialog({ open, title, children, confirmLabel, onConfirm, onCancel, busy }: {
  open: boolean; title: string; children: ReactNode; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void; busy?: boolean;
}) {
  return (
    <Modal open={open} title={title} onClose={onCancel}
      footer={<>
        <Button variant="ghost" onClick={onCancel}>{t("Annuler")}</Button>
        <Button variant="danger" onClick={onConfirm} disabled={busy}>{confirmLabel}</Button>
      </>}>
      <div className="text-muted">{children}</div>
    </Modal>
  );
}

// ---------------------------------------------------------------- formulaires
export function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="font-cond text-[13px] font-semibold uppercase tracking-wider text-muted">{label}</label>
      {children}
      {hint && <p className="text-[12px] text-faint">{hint}</p>}
    </div>
  );
}

const control = "h-9 w-full rounded-md border border-line bg-bg px-3 text-ink placeholder:text-faint focus:border-accent focus:outline-none disabled:opacity-50";

export function TextInput({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(control, className)} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(control, "pr-8", className)}>{children}</select>;
}

export function Checkbox({ label, checked, onChange, id }: { label: string; checked: boolean; onChange: (v: boolean) => void; id: string }) {
  return (
    <label htmlFor={id} className="inline-flex cursor-pointer items-center gap-2 select-none">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
        className="size-4 accent-[var(--color-accent)]" />
      {label}
    </label>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-md border border-line bg-bg p-0.5">
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} onClick={() => onChange(o.value)}
          className={cx("h-8 rounded px-3 cursor-pointer transition-colors",
            value === o.value ? "bg-raised text-ink" : "text-muted hover:text-ink")}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return <p role="alert" className="rounded-md border border-err/40 bg-err/10 px-3 py-2 text-err">{message}</p>;
}

// ---------------------------------------------------------------- copier
export function CopyField({ value, label }: { value: string; label?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      ref.current?.select(); // presse-papiers refusé (HTTP hors localhost) : on sélectionne le texte
    }
  };
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      {label && <span className="w-14 shrink-0 font-mono text-[11px] uppercase text-faint">{label}</span>}
      <input ref={ref} readOnly value={value} aria-label={label ?? "URL"} onFocus={(e) => e.target.select()}
        className="h-8 min-w-0 flex-1 rounded border border-line bg-bg px-2 font-mono text-[12px] text-ink" />
      <Button size="sm" onClick={copy} aria-label={t("Copier")}>{copied ? t("Copié") : t("Copier")}</Button>
    </div>
  );
}

// ---------------------------------------------------------------- notifications
interface Toast { id: number; message: string; tone: Tone }
const ToastContext = createContext<(message: string, tone?: Tone) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((message: string, tone: Tone = "ok") => {
    const id = Date.now() + Math.random();
    setToasts((list) => [...list, { id, message, tone }]);
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), tone === "err" ? 7000 : 3500);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div aria-live="polite" className="fixed right-4 bottom-4 z-50 flex max-w-sm flex-col gap-2">
        {toasts.map((toast) => (
          <div key={toast.id} className="flex items-start gap-2 rounded-md border border-line bg-raised px-3 py-2 shadow-xl">
            <span className="mt-1.5"><Dot tone={toast.tone} /></span>
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
